<script setup>
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import teamService from "@/services/team.service"
import TeamDrawer from '@/views/feedback/master/TeamDrawer.vue'
import { VDataTableServer } from 'vuetify/labs/VDataTable'

const searchQuery = ref('')
const debouncedSearch = refDebounced(searchQuery, 500)
const isConfirmDialogVisible = ref(false)

definePage({
  meta: {
    name: 'master-team',
    subject: 'feedback.menu',
    action: 'master_team',
  },
})

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const errors = ref({
  name: [],
  code: [],
  status: [],
})

const onError = error => {
  if (error.data){
    errors.value = error.data.errors
  }
}

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order.toUpperCase()
}

// Headers
const headers = [
  {
    title: 'Name',
    key: 'name',
  },
  {
    title: 'Actions',
    key: 'actions',
    sortable: false,
  },
]

const {
  data: teamsData,
  execute: fetchTeams,
  isFetching: isFetchingTeams,
} = await useApi(createUrl('/masters/teams', {
  query: {
    paged: 1,
    q: debouncedSearch,
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
  },
}))

const teams = computed(() => teamsData.value?.data)
const totalTeams = computed(() => teamsData.value?.meta?.total)

const updateTeam = data => {
  if (data.id){
    teamService.updateTeam(data.id, data, () => {
      fetchTeams()
      isTeamDrawerVisible.value = false
      resetErrors()
    }, onError)
  }
}

const saveTeam = data => {
  teamService.addTeam(data, () => {
    fetchTeams()
    isTeamDrawerVisible.value = false
    resetErrors()
  }, onError)
}

const resetErrors = () => {
  errors.value = {
    name: [],
    code: [],
    status: [],
  }
}

const isTeamDrawerVisible = ref(false)
const teamDrawerData = ref()
const teamDeleteData = ref()
const teamDrawerType = ref('Add')

const showTeam = (teamData, type) => {
  teamDrawerData.value = { ...teamData }
  teamDrawerType.value = type
  isTeamDrawerVisible.value = true
}

const deleteTeam = async (confirm, data) => {
  if(data?.id && confirm ){
    await teamService.deleteTeam(data.id, () => {
      fetchTeams()
      isConfirmDialogVisible.value = false
    }, onError)
  }
}

const showConfirmDialog = teamData => {
  teamDeleteData.value = teamData
  isConfirmDialogVisible.value = true
}

watch(isConfirmDialogVisible, () => {
  if (!isConfirmDialogVisible.value){
    teamDeleteData.value = {}
  }
})
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border"
    >
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <!-- 👉 Left side: Filters -->
        <div class="d-flex align-center flex-wrap gap-4 filter-section">
          <div class="filter-field filter-field--search">
            <AppTextField
              v-model="searchQuery"
              placeholder="Search"
              density="compact"
            />
          </div>
        </div>

        <VSpacer />

        <!-- 👉 Right side: Action Buttons -->
        <div class="d-flex align-center flex-wrap gap-4">
          <VBtn
            v-if="$can('create', 'master.team')"
            prepend-icon="tabler-plus"
            variant="flat"
            @click="showTeam({}, 'Add')"
          >
            Add Team
          </VBtn>

          <IconBtn
            variant="tonal"
            class="rounded"
            :loading="isFetchingTeams"
            @click="fetchTeams"
          >
            <VIcon
              size="24"
              icon="tabler-refresh"
            />
          </IconBtn>
        </div>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="teams"
        :items-length="totalTeams"
        :headers="headers"
        :loading="isFetchingTeams"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.name="{ item }">
          <span
            class="text-base font-weight-medium cursor-pointer"
            @click="showTeam(item, 'View')"
          >
            {{ item.name }}
          </span>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <IconBtn
            v-if="$can('delete', 'master.team')"
            @click="showConfirmDialog(item)"
          >
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <IconBtn
            v-if="$can('update', 'master.team')"
            @click="showTeam(item, 'Edit')"
          >
            <VIcon icon="tabler-edit" />
          </IconBtn>
        </template>
      </VDataTableServer>
    </VCard>
    <TeamDrawer
      v-model:isDrawerOpen="isTeamDrawerVisible"
      :team-data="teamDrawerData"
      :type="teamDrawerType ?? 'Add'"
      :errors="errors"
      @update:type="teamDrawerType = $event"
      @update:team-data="updateTeam"
      @save-team="saveTeam"
    />
    <ConfirmDialog
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure you want to delete this team?"
      confirm-msg="Team has been deleted successfully."
      confirm-title="Team Deleted!"
      :data="teamDeleteData"
      @confirm="deleteTeam"
    />
  </section>
</template>

<style scoped>
.filter-field {
  inline-size: 15rem;
}

.filter-field--search {
  inline-size: 15rem;
}

@media (max-width: 600px) {
  .filter-section {
    inline-size: 100%;
  }

  .filter-field,
  .filter-field--search {
    inline-size: 100%;
  }
}

:deep(.v-data-table-footer) {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}
</style>
